<template>
  <div class="p-4 surface-ground min-h-screen">
    <Toast />
    <div class="flex justify-content-between align-items-center mb-4">
      <div>
        <span class="text-sm font-bold text-orange-500 mb-1 block">COCINA</span>
        <h2 class="m-0 text-3xl font-bold text-900">Orden de Producción</h2>
      </div>
      <div class="flex gap-3">
        <Button label="Cancelar" icon="pi pi-times" class="p-button-secondary p-button-outlined" @click="cancelar" />
        <Button label="Guardar Orden" icon="pi pi-check-circle" class="p-button-primary bg-orange-600 border-none hover:bg-orange-700" :loading="guardando" :disabled="guardando" @click="guardarOrden" />
      </div>
    </div>

    <div class="grid">
      <!-- LADO IZQUIERDO: FORMULARIO DE TRABAJO (65% ancho) -->
      <div class="col-12 lg:col-8">
        
        <!-- CABECERA: ORDEN -->
        <Card class="mb-4 shadow-1 border-round-xl">
          <template #content>
            <div class="grid p-fluid">
              <div class="col-12 md:col-4">
                <label class="font-semibold block mb-2 text-sm text-700">Sucursal <span class="text-red-500">*</span></label>
                <DropdownSucursal v-model="orden_produccion.sucursal_id" class="p-inputtext-sm" @change="onSucursalChange" />
              </div>
              <div class="col-12 md:col-4">
                <label class="font-semibold block mb-2 text-sm text-700">Código de Lote <span class="text-red-500">*</span></label>
                <InputText v-model="orden_produccion.codigo_lote" placeholder="Ej. LOTE-001" class="p-inputtext-sm" />
              </div>
              <div class="col-12 md:col-4">
                <label class="font-semibold block mb-2 text-sm text-700">Motivo de Merma</label>
                <InputText v-model="orden_produccion.motivo_merma" placeholder="Ej. Recorte, sobrefermentada..." class="p-inputtext-sm" />
              </div>
            </div>
          </template>
        </Card>

        <!-- RESULTADO: QUÉ VAMOS A PRODUCIR -->
      

        <!-- MATERIA PRIMA A CONSUMIR -->
        <Card class="mb-4 shadow-1 border-round-xl">
          <template #title>
            <div class="flex justify-content-between align-items-center mb-2 border-bottom-1 surface-border pb-3">
              <span class="text-lg font-bold text-800"><i class="pi pi-arrow-down mr-2 text-red-500"></i>MATERIA PRIMA A CONSUMIR <span class="text-red-500 text-sm font-normal">(lo que se gasta)</span></span>
              <Button label="Añadir Materia Prima" icon="pi pi-plus" class="p-button-outlined p-button-secondary p-button-sm" @click="addInsumo" />
            </div>
          </template>
          <template #content>
            <DataTable :value="insumosLocales" responsiveLayout="scroll" class="p-datatable-sm" emptyMessage="No hay insumos.">
              <Column header="Materia Prima">
                <template #body="{ data, index }">
                  <DropdownProducto v-model="data.producto_id" :es-producido="0" :es-insumo="1" class="w-full p-inputtext-sm" @change="onInsumoChange(index)"  />
                  <small class="text-500 block mt-1" v-if="data.disponible !== undefined">
                    Disponible: {{ data.disponible }}g &middot; Bs {{ (data.costo_unitario || 0).toFixed(2) }}/Kg (Bs {{ ((data.costo_unitario || 0) / 1000).toFixed(4) }}/g)
                  </small>
                </template>
              </Column>
              <Column header="Total a Consumir (g)" style="width: 14rem">
                <template #body="{ data }">
                  <ZapiInputNumber v-model="data.cantidad_gramo" class="m-0 p-inputtext-sm w-full" @update:modelValue="onCantidadChange(data)" />
                  <small class="text-red-500 block mt-1" v-if="data.disponible !== undefined && data.cantidad_gramo > data.disponible">
                    ⚠ Supera el disponible ({{ data.disponible }}g)
                  </small>
                </template>
              </Column>
              <!-- Cantidad Merma -->
              <Column header="Merma (g)" style="width: 10rem">
                <template #body="{ data }">
                  <ZapiInputNumber 
                    v-model="data.cantidad_merma" 
                    class="m-0 p-inputtext-sm w-full" 
                    :min="0"
                    :max="data.cantidad_gramo || 0"
                    @update:modelValue="onCantidadChange(data)" 
                  />
                </template>
              </Column>
              <!-- Selector Tipo de Merma -->
              <Column header="Tipo Merma" style="width: 13rem">
                <template #body="{ data }">
                  <Dropdown 
                    v-model="data.tipo_merma_id" 
                    :options="tiposMerma" 
                    optionLabel="name" 
                    optionValue="id" 
                    placeholder="Seleccionar..." 
                    class="w-full p-inputtext-sm"
                    :disabled="!data.cantidad_merma || data.cantidad_merma <= 0"
                  />
                  <small class="text-orange-500 block mt-1" v-if="data.cantidad_merma > 0 && !data.tipo_merma_id">
                    * Seleccione tipo
                  </small>
                </template>
              </Column>
              <!-- Neto Útil (No editable) -->
              <Column header="Neto Útil (g)" style="width: 9rem">
                <template #body="{ data }">
                  <span class="font-bold text-teal-600">
                    {{ (data.cantidad_real || 0).toLocaleString() }} g
                  </span>
                </template>
              </Column>
              <Column header="CPP (Bs/Kg)" style="width: 8rem">
                <template #body="{ data }">
                  <span class="text-600">Bs {{ (data.costo_unitario || 0).toFixed(2) }}</span>
                </template>
              </Column>
              <Column header="Costo Insumo" style="width: 10rem">
                <template #body="{ data }">
                  <span class="font-bold text-900">Bs {{ (data.costo_subtotal || 0).toFixed(2) }}</span>
                </template>
              </Column>
              <Column header="" style="width: 3rem; text-align: center">
                <template #body="{ index }">
                  <Button icon="pi pi-trash" class="p-button-rounded p-button-danger p-button-text" @click="removeInsumo(index)" />
                </template>
              </Column>
            </DataTable>
          </template>
        </Card>
          <Card class="mb-4 shadow-1 border-round-xl">
          <template #title>
            <div class="flex justify-content-between align-items-center mb-2 border-bottom-1 surface-border pb-1">
              <span class="text-lg font-bold text-800"><i class="pi pi-arrow-up mr-2 text-green-500"></i>PRODUCTO RESULTANTE <span class="text-green-600 text-sm font-normal">(lo que se crea)</span></span>
            </div>
          </template>
          <template #content>
            <div class="grid p-fluid align-items-end">
              <!-- Fila 1: Producto Resultante y Vencimiento -->
              <div class="col-12 md:col-8">
                <label class="font-semibold block mb-2 text-sm text-700 flex align-items-end" style="min-height: 2.2rem;">Producto Resultante / Porcionado <span class="text-red-500 ml-1">*</span></label>
                <DropdownProducto v-model="productoResultado.producto_id" :es-producido="1" :es-insumo="1" class="p-inputtext-sm" @change="onProductoResultanteChange" />
              </div>
              <div class="col-12 md:col-4">
                <label class="font-semibold block mb-2 text-sm text-700 flex align-items-end" style="min-height: 2.2rem;">Vencimiento Lote <span class="text-red-500 ml-1">*</span></label>
                <Calendar v-model="fechaVencimientoResultado" :minDate="new Date()" dateFormat="dd/mm/yy" class="p-inputtext-sm w-full" placeholder="dd/mm/yyyy" />
              </div>

              <!-- Fila 2: Gramos y Porciones Obtenidas -->
              <div class="col-12 md:col-4">
                <label class="font-semibold block mb-2 text-sm text-700 flex align-items-end" style="min-height: 2.2rem;">Gramos por Porción</label>
                <ZapiInputNumber v-model="gramajeUnidad" class="p-inputtext-sm w-full" />
              </div>
              <div class="col-12 md:col-4">
                <label class="font-semibold block mb-2 text-sm text-700 flex align-items-end" style="min-height: 2.2rem;">Cant. Porciones Obtenidas</label>
                <ZapiInputNumber :key="cantidadReal" v-model="cantidadReal" class="p-inputtext-sm w-full" @update:modelValue="cantidadRealTocadaManualmente = true" />
              </div>              

              <!-- BLOQUE DESTACADO PORCIONES SUGERIDAS -->
              <div v-if="porcionesSugeridas !== '-'" class="col-12 mt-3">
                <div class="surface-100 border-round p-4 flex align-items-center gap-3">
                  <i class="pi pi-box text-3xl text-orange-500"></i>
                  <div>
                    <span class="text-sm text-700 block">Vas a obtener aproximadamente</span>
                    <span class="text-2xl font-bold text-900">{{ porcionesSugeridas }} porciones</span>
                  </div>
                </div>
              </div>
            </div>
          </template>
        </Card>
      </div>

      <!-- LADO DERECHO: PANEL DASHBOARD (35% ancho) -->
      <div class="col-12 lg:col-4 flex flex-column gap-4">
        
        <!-- RESUMEN FINANCIERO -->
        <Card class="shadow-1 border-round-xl">
          <template #title>
            <div class="text-base font-bold text-800 border-bottom-1 surface-border pb-2">RESUMEN DE PRODUCCIÓN</div>
          </template>
          <template #content>
            <div class="flex justify-content-between mb-3 text-700">
              <span>Costo Total Insumos</span>
              <span class="font-semibold">Bs {{ costoTotalInsumos.toFixed(2) }}</span>
            </div>

            <Divider />
            <div class="flex justify-content-between align-items-center mt-3">
              <span class="font-bold text-800 text-lg">Costo Unit. Resultante</span>
              <span class="font-bold text-red-600 text-2xl">Bs {{ costoUnitarioResultante }}</span>
            </div>
          </template>
        </Card>
        
      </div>
    </div>
  </div>
</template>

<script setup>
import DropdownSucursal from '@/components/shared/Dropdown/DropdownSucursal.vue';
import DropdownProducto from '@/components/shared/Dropdown/DropdownProducto.vue';
import ZapiInputNumber from '@/components/shared/ZapiInputNumber.vue';
import ProduccionService from '@/service/ProduccionService';
import StockService from '@/service/StockService';
import ProductService from '@/service/ProductService';
import TipoMermaService from '@/service/TipoMermaService';
import { ref, reactive, computed, watch, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useToast } from "primevue/usetoast";

const router = useRouter();
const toast = useToast();
const produccionService = new ProduccionService();
const tipoMermaService = new TipoMermaService();
const stockService = new StockService();
const productService = new ProductService();

const guardando = ref(false);

const orden_produccion = reactive({
  sucursal_id: null,
  codigo_lote: '',
  motivo_merma: ''
});

const productoResultado = reactive({
  producto_id: null,
  producto_detalle: null
});
const cantidadReal = ref(1);
const cantidadRealTocadaManualmente = ref(false);
const gramajeUnidad = ref(null);
const fechaVencimientoResultado = ref(null);

const insumosLocales = ref([]);
const tiposMerma = ref([]);

onMounted(async () => {
  try {
    const response = await tipoMermaService.getTipoMermaCombo();
    tiposMerma.value = response.data || response || [];
  } catch (e) {
    console.error("Error al cargar los tipos de merma:", e);
  }
});

const porcionesSugeridas = computed(() => {
  if(!gramajeUnidad.value || gramajeUnidad.value <= 0) return '-';
  // Se suman únicamente los gramos reales aprovechables
  const totalGramosUtiles = insumosLocales.value.reduce(
    (sum, i) => sum + (parseFloat(i.cantidad_real) || 0), 0
  );

  return Math.floor(totalGramosUtiles / gramajeUnidad.value);
});

watch(porcionesSugeridas, (nuevas) => {
  if (!cantidadRealTocadaManualmente.value && typeof nuevas === 'number' && nuevas > 0) {
    cantidadReal.value = nuevas;
  }
});

const onProductoResultanteChange = async () => {
  const id = productoResultado.producto_id;
  if (!id) {
    productoResultado.producto_detalle = null;
    return;
  }

  // Autocompletar automáticamente fecha_vencimiento con fecha actual + 7 días
  const fechaCalc = new Date();
  fechaCalc.setDate(fechaCalc.getDate() + 7);
  fechaVencimientoResultado.value = fechaCalc;

  try {
    const response = await productService.showProducto(id);
    productoResultado.producto_detalle = response.producto || response;
  } catch (e) {
    console.error(e);
  }
};

const onSucursalChange = () => {
  insumosLocales.value.forEach((insumo, index) => {
    if (insumo.producto_id) {
      onInsumoChange(index);
    }
  });
};

const onInsumoChange = async (index) => {
  const insumo = insumosLocales.value[index];
  if (!insumo.producto_id || !orden_produccion.sucursal_id) return;

  try {
    const response = await stockService.consultarCostoInsumo(insumo.producto_id, orden_produccion.sucursal_id);
    if (response.success) {
      insumo.costo_unitario = response.cpp;
      insumo.disponible = response.cantidad_gramos ?? 0;
      calcularSubtotal(insumo);
    }
  } catch (e) {
    console.error("Error al obtener CPP", e);
  }
};

const calcularSubtotal = (insumo) => {
  const bruto = parseFloat(insumo.cantidad_gramo) || 0;
  const cpp = parseFloat(insumo.costo_unitario) || 0;
  insumo.costo_subtotal = (bruto / 1000) * cpp;
};

const addInsumo = () => {
  insumosLocales.value.push({ 
    producto_id: null, 
    cantidad_gramo: 0, 
    cantidad_merma: 0,
    cantidad_real: 0,
    tipo_merma_id: null,
    costo_unitario: 0, 
    costo_subtotal: 0, 
    lote_consumido: null
  });
}
const removeInsumo = (index) => insumosLocales.value.splice(index, 1);

const costoTotalInsumos = computed(() => {
  return insumosLocales.value.reduce((total, insumo) => total + (insumo.costo_subtotal || 0), 0);
});

const costoUnitarioResultante = computed(() => {
  if (cantidadReal.value === 0) return "0.00";
  return (costoTotalInsumos.value / cantidadReal.value).toFixed(2);
});

const cancelar = () => router.push('/produccion');

const guardarOrden = async () => {
  if (guardando.value) return;
  if (!orden_produccion.sucursal_id) {
    toast.add({ severity: 'error', summary: 'Error', detail: 'Debe seleccionar una sucursal.', life: 3000 });
    return;
  }
  if (!orden_produccion.codigo_lote) {
    toast.add({ severity: 'error', summary: 'Error', detail: 'El código de lote es obligatorio.', life: 3000 });
    return;
  }
  if (!productoResultado.producto_id) {
    toast.add({ severity: 'error', summary: 'Error', detail: 'Debe seleccionar un producto resultante.', life: 3000 });
    return;
  }
  if (!fechaVencimientoResultado.value) {
    toast.add({ severity: 'error', summary: 'Error', detail: 'Debe ingresar la fecha de vencimiento del lote.', life: 3000 });
    return;
  }
  if (insumosLocales.value.length === 0) {
    toast.add({ severity: 'error', summary: 'Error', detail: 'Debe añadir al menos un insumo.', life: 3000 });
    return;
  }
  
  // Validar que cada insumo con merma registrada tenga un Tipo de Merma seleccionado
  const mermaInvalida = insumosLocales.value.some(
    i => (parseFloat(i.cantidad_merma) || 0) > 0 && !i.tipo_merma_id
  );
  if (mermaInvalida) {
    toast.add({
      severity: 'warn',
      summary: 'Atención',
      detail: 'Debe seleccionar un Tipo de Merma para cada insumo con merma registrada.',
      life: 4000
    });
    return;
  }

  // Validar motivo de merma si hay diferencia entre planificado y real
  const planificado = typeof porcionesSugeridas.value === 'number' ? porcionesSugeridas.value : cantidadReal.value;
  if (cantidadReal.value !== planificado && !orden_produccion.motivo_merma) {
    toast.add({ severity: 'error', summary: 'Error', detail: 'Hubo diferencia entre lo planificado y lo obtenido — indicá el motivo de merma.', life: 3000 });
    return;
  }

  guardando.value = true;
  
  let fv = null;
  if (fechaVencimientoResultado.value) {
    const d = new Date(fechaVencimientoResultado.value);
    fv = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
  }

  const payload = {
    sucursal_id: orden_produccion.sucursal_id,
    producto_id: productoResultado.producto_id,
    cantidad_planificada: typeof porcionesSugeridas.value === 'number' ? porcionesSugeridas.value : cantidadReal.value,
    cantidad_real: cantidadReal.value,
    gramaje_unidad: gramajeUnidad.value || null,
    costo_total: costoTotalInsumos.value,
    costo_unitario_resultante: parseFloat(costoUnitarioResultante.value),
    codigo_lote: orden_produccion.codigo_lote,
    fecha_vencimiento: fv,
    motivo_merma: orden_produccion.motivo_merma,
    insumos: insumosLocales.value.map(i => ({
      producto_id: i.producto_id,
      lote_consumido: i.lote_consumido,
      cantidad_gramo: parseFloat(i.cantidad_gramo || 0),
      cantidad_merma: parseFloat(i.cantidad_merma || 0),
      cantidad_real: parseFloat(i.cantidad_real || 0),
      tipo_merma_id: i.tipo_merma_id || null,
      costo_unitario: parseFloat(i.costo_unitario || 0),
      costo_subtotal: parseFloat(i.costo_subtotal || 0)
    }))
  };

  try {
    const response = await produccionService.crearOrdenProduccion(payload);
    if (response) {
      toast.add({ severity: 'success', summary: 'Éxito', detail: 'Orden registrada correctamente.', life: 2000 });
      setTimeout(() => router.push('/produccion'), 1200);
    }
  } catch (error) {
    toast.add({ severity: 'error', summary: 'Error', detail: error || 'No se pudo guardar la orden.', life: 5000 });
  } finally {
    guardando.value = false;
  }
};

const onCantidadChange = (insumo) => {
  const bruto = parseFloat(insumo.cantidad_gramo) || 0;
  const merma = parseFloat(insumo.cantidad_merma) || 0;

  // El neto real aprovechado es el bruto menos la merma
  insumo.cantidad_real = Math.max(0, bruto - merma);

  // Si la merma vuelve a 0, limpiamos el tipo seleccionado
  if (merma <= 0) {
    insumo.tipo_merma_id = null;
  }

  // El costo monetario lo absorbe el lote completo (bruto * CPP)
  calcularSubtotal(insumo);
};
</script>
